/**
 * useTvdbImages -- Hook for fetching TheTVDB canonical artwork.
 *
 * Calls /api/streaming/tvdb/images when a TVDB ID is available,
 * returning the best poster and backdrop file paths for proxy URLs.
 */

import { useEffect, useState } from "react";

import { useCoverArtContext } from "../contexts/CoverArtSettingsContext";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TvdbImageData {
  bestPoster: { filePath: string; width: number; height: number } | null;
  bestBackdrop: { filePath: string; width: number; height: number } | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// URL Builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a proxy URL for a TVDB image.
 * TVDB has no resize support — images are served at native resolution.
 *
 * @param filePath - TVDB file path (e.g. "posters/121361-5.jpg")
 * @param type - "poster" | "backdrop"
 */
export function buildTvdbProxyUrl(
  filePath: string,
  type: "poster" | "backdrop",
): string {
  const params = new URLSearchParams({ path: filePath, type });
  return `/api/streaming/tvdb/image?${params.toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch TVDB canonical images for a media item.
 *
 * @param tvdbId - TVDB ID from providerIds, or null/undefined if unavailable
 * @param mediaType - "movie" | "tv"
 */
export function useTvdbImages(
  tvdbId: string | null | undefined,
  mediaType: string | null | undefined,
): { tvdbImages: TvdbImageData | null; tvdbLoading: boolean } {
  const { tvdbEnabled } = useCoverArtContext();
  const [tvdbImages, setTvdbImages] = useState<TvdbImageData | null>(null);
  const [tvdbLoading, setTvdbLoading] = useState(false);

  useEffect(() => {
    if (!tvdbEnabled || !tvdbId || !mediaType) {
      setTvdbImages(null);
      return;
    }

    let cancelled = false;
    setTvdbLoading(true);

    const params = new URLSearchParams({ tvdbId, mediaType });

    fetch(`/api/streaming/tvdb/images?${params.toString()}`, {
      headers: { Accept: "application/json" },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((json: { ok?: boolean; data?: TvdbImageData }) => {
        if (cancelled) return;
        if (json.ok && json.data) {
          setTvdbImages(json.data);
        } else {
          setTvdbImages(null);
        }
      })
      .catch(() => {
        if (!cancelled) setTvdbImages(null);
      })
      .finally(() => {
        if (!cancelled) setTvdbLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tvdbEnabled, tvdbId, mediaType]);

  return { tvdbImages, tvdbLoading };
}
